import React from "react";
import { motion } from "framer-motion";

const defaultVariants = {
  hidden: { 
    opacity: 0, 
    y: 30, 
    filter: "blur(10px)",
    scale: 0.98 
  },
  visible: { 
    opacity: 1, 
    y: 0, 
    filter: "blur(0px)",
    scale: 1,
    transition: {
      duration: 1.2, // Slower, more cinematic
      ease: [0.22, 1, 0.36, 1] 
    }
  }
};

const glitchVariants = {
  hidden: { 
    opacity: 0,
    x: -20,
    skewX: 20
  },
  visible: {
    opacity: 1,
    x: 0,
    skewX: 0,
    transition: {
      duration: 0.8, // Slower glitch entry
      ease: "circOut",
      type: "spring",
      stiffness: 70 // Softer spring
    }
  }
};

const hologramVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
    // Removed blur(20px) - it caused massive GPU lag on large WebGL canvas
    filter: "brightness(0)" 
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    filter: "brightness(1)",
    transition: {
      duration: 2,
      ease: "anticipate",
      opacity: {
        duration: 1.5,
        times: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 1],
        values: [0, 0.3, 0.1, 0.5, 0.2, 0.8, 0.4, 1]
      }
    }
  }
};

export const ScrollReveal = ({ 
  children, 
  className = "", 
  variant = "auto", // Default to auto-random selection
  delay = 0,
  viewportAmount = 0.2,
  onClick // Forward onClick to motion.div
}) => {
  const [isInView, setIsInView] = React.useState(false);

  // Randomize variant on mount if set to 'auto'
  // 30% chance of Glitch, 70% chance of Smooth Fade (Default)
  // If Glitch, randomly choose between 3 styles
  const [resolvedVariant, setResolvedVariant] = React.useState("default");
  const [glitchClass, setGlitchClass] = React.useState("");
  
  React.useEffect(() => {
    // If specific variant is requested (like 'hologram'), prioritize it immediately
    if (variant === "hologram") {
      setResolvedVariant("hologram");
      return;
    }

    if (variant === "auto" || variant === "glitch") {
      // ALWAYS use glitch for auto/glitch, no more "default" fade
      setResolvedVariant("glitch");
      // Pick random glitch style 1-6 (Mix of Cinematic & Data Mosh)
      const styleIdx = Math.floor(Math.random() * 6) + 1; 
      setGlitchClass(`animate-glitch-${styleIdx}`);
    } else {
       // Only if someone specifically passes "default" (unlikely now)
       setResolvedVariant(variant);
    }
  }, [variant]);

  let activeVariantConfig = defaultVariants;
  if (resolvedVariant === "glitch") activeVariantConfig = glitchVariants;
  if (resolvedVariant === "hologram") activeVariantConfig = hologramVariants;

  // Combine provided className with the glitch animation class if active and in view
  const combinedClassName = `${className} ${
    resolvedVariant === "glitch" && isInView ? glitchClass : ""
  }`;

  // Random Delay for the Glitch Animation itself (to avoid sync)
  const randomAnimationDelay = React.useMemo(() => {
     return Math.random() * 0.5; // 0 to 0.5s random delay
  }, []);

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: viewportAmount, margin: "0px 0px -100px 0px" }}
      onViewportEnter={() => {
        setIsInView(true);
        // Re-roll the random glitch style every time it enters the viewport
        if (resolvedVariant === "glitch") {
           const styleIdx = Math.floor(Math.random() * 6) + 1; 
           setGlitchClass(`animate-glitch-${styleIdx}`);
        }
      }}
      onViewportLeave={() => setIsInView(false)}
      variants={activeVariantConfig}
      transition={{ delay: delay }}
      className={combinedClassName}
      onClick={onClick}
      style={{
         // If it's a glitch variant, we enforce opacity:1 via framer but let CSS handle the flicker entry
         // We add the random animation delay dynamically
         animationDelay: `${randomAnimationDelay}s` 
      }}
    >
      {children}
    </motion.div>
  );
};

